// Renders the document toolbar's plugin-contributed header buttons (manifest
// `contributes.topBarActions`, placement 'beforeHelp'). Each button runs its
// declared command through the host. Subscribes to contribution changes so
// actions appear/disappear live. Icon names are resolved through the safe lucide
// lookup (Puzzle fallback) so a bad manifest cannot crash the toolbar.

import { useSyncExternalStore, type ReactElement } from 'react';
import { Puzzle } from 'lucide-react';
import { resolveContributionIcon } from '../tools/contributionIcon';
import type { PluginHost } from './PluginHost';

export interface PluginTopBarActionsProps {
  host: Pick<
    PluginHost,
    'listTopBarActions' | 'onDidChangeContributions' | 'contributionVersion' | 'runCommand'
  >;
}

export function PluginTopBarActions({ host }: PluginTopBarActionsProps): ReactElement | null {
  useSyncExternalStore(
    (cb) => host.onDidChangeContributions(cb),
    () => host.contributionVersion(),
    () => host.contributionVersion(),
  );
  const actions = host.listTopBarActions();
  if (actions.length === 0) return null;
  return (
    <>
      {actions.map((a) => {
        const Icon = resolveContributionIcon(a.icon) ?? Puzzle;
        return (
          <button
            key={`${a.pluginId}:${a.id}`}
            type="button"
            className="mp-help-icon mp-topbar-action"
            aria-label={a.title}
            title={a.title}
            onClick={() => void host.runCommand(a.pluginId, a.command)}
          >
            <Icon size={13} aria-hidden strokeWidth={2} />
          </button>
        );
      })}
    </>
  );
}
